import { Link } from 'react-router-dom';
import type { Competitor, FeatureClaim, FeatureDefinition } from './types';

const LABELS: Record<FeatureClaim['value'], string> = {
  yes: 'Yes',
  no: 'No',
  partial: 'Partly',
  unknown: 'Not verified',
};

function tone(claim: FeatureClaim, inverted?: boolean) {
  if (claim.value === 'partial') return 'text-amber-700';
  if (claim.value === 'unknown') return 'text-slate-400';
  const good = inverted ? claim.value === 'no' : claim.value === 'yes';
  return good ? 'text-emerald-700' : 'text-slate-500';
}

function icon(value: FeatureClaim['value'], inverted?: boolean) {
  if (value === 'yes') return inverted ? '✕' : '✓';
  if (value === 'no') return inverted ? '✓' : '✕';
  if (value === 'partial') return '◐';
  return '?';
}

function isExternal(url: string) {
  return /^https?:\/\//.test(url);
}

function Cell({ claim, inverted }: { claim?: FeatureClaim; inverted?: boolean }) {
  const value = claim ?? { value: 'unknown' as const };
  return (
    <td className="border-t border-slate-200 px-4 py-3 align-top">
      <span className={`inline-flex items-center gap-1.5 font-semibold ${tone(value, inverted)}`}>
        <span aria-hidden="true">{icon(value.value, inverted)}</span>
        {value.badge ?? LABELS[value.value]}
      </span>
      {value.note && <p className="mt-1 text-sm leading-snug text-slate-600">{value.note}</p>}
      {value.sourceUrl && (
        <a
          href={value.sourceUrl}
          {...(isExternal(value.sourceUrl) ? { target: '_blank', rel: 'noopener noreferrer nofollow' } : {})}
          className="mt-1 inline-block text-xs text-slate-400 underline underline-offset-2 hover:text-[#1a4e94]"
        >
          source
        </a>
      )}
    </td>
  );
}

interface ComparisonTableProps {
  ours: Competitor;
  theirs: Competitor;
  features: FeatureDefinition[];
  caption?: string;
}

export default function ComparisonTable({ ours, theirs, features, caption }: ComparisonTableProps) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <caption className="sr-only">{caption ?? 'Feature comparison'}: {ours.name} and {theirs.name}</caption>
        <thead className="bg-slate-50">
          <tr>
            <th scope="col" className="w-1/4 px-4 py-3 text-sm font-semibold text-slate-500">Feature</th>
            <th scope="col" className="px-4 py-3 text-sm font-semibold text-slate-900">{ours.name}</th>
            <th scope="col" className="px-4 py-3 text-sm font-semibold text-slate-900">{theirs.name}</th>
          </tr>
        </thead>
        <tbody>
          {features.map((feature) => (
            <tr key={feature.key}>
              <th scope="row" className="border-t border-slate-200 px-4 py-3 align-top">
                {feature.href
                  ? <Link to={feature.href} className="font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:text-[#1a4e94]">{feature.label}</Link>
                  : <span className="font-semibold text-slate-900">{feature.label}</span>}
                <p className="mt-1 text-sm font-normal leading-snug text-slate-500">{feature.description}</p>
              </th>
              <Cell claim={ours.features[feature.key]} inverted={feature.inverted} />
              <Cell claim={theirs.features[feature.key]} inverted={feature.inverted} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
